import React, { useState } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { toast } from 'react-toastify';
import AddCustomization from './AddCustomization';
import { useParams } from 'react-router-dom';
import { Button, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

function CustomizationForm({ customization, onDataChange, onSaveComplete, onCancel }) {
    const [customValue, setCustomValue] = useState(customization);
    const { clinicId } = useParams();
    const [modify, setModify] = useState(false);
    const [copied, setCopied] = useState(null);
    const [addNew, setAddNew] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleDelete = (item) => {
        clinicService
            .deleteCustomization(item.clinicId, item.id)
            .then(() => {
                const updatedValues = customValue.filter((cv) => cv.id !== item.id);
                setCustomValue(updatedValues);
                onDataChange?.(updatedValues);
                toast.success('Customization deleted', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .catch(() => {
                toast.error('Error while deleting', { position: toast.POSITION.BOTTOM_RIGHT });
            });
    };

    const handleEdit = (item, source, mytarget) => {
        const updatedValues = customValue.map((cv) => {
            if (source === 'optionkey' && cv.id === item.id) {
                return { ...cv, optionKey: mytarget.target.value };
            }
            if (source === 'optionvalue' && cv.id === item.id) {
                return { ...cv, optionValue: mytarget.target.value };
            }
            return cv;
        });
        setCustomValue(updatedValues);
    };

    const handleSave = () => {
        setIsSaving(true);
        clinicService
            .updateCustomization(clinicId, customValue)
            .then((res) => {
                toast.success('Customization saved', { position: toast.POSITION.BOTTOM_RIGHT });
                setCustomValue(res.data);
                onSaveComplete?.(res.data);
                setModify(false);
            })
            .catch(() => {
                toast.error('Error saving customization', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const copyToClipboard = (id) => {
        navigator.clipboard.writeText(String(id)).then(() => {
            setCopied(id);
            setTimeout(() => setCopied(null), 2000);
        });
    };

    const handleCustomAdd = (newItems) => {
        if (!Array.isArray(newItems) || newItems.length === 0) return;

        const existingIds = new Set(customValue.map((c) => c.id));
        const additions = newItems.filter((item) => item.id && !existingIds.has(item.id));
        const merged = additions.length > 0 ? [...customValue, ...additions] : [...customValue, newItems[0]];

        setCustomValue(merged);
        onDataChange?.(merged);
        setAddNew(false);
    };

    return (
        <div className="row g-3 clinic-customization-edit-row">
            <div className="col-lg-8">
                <div className="notifications-panel">
                    <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                        <span className="d-flex align-items-center gap-2">
                            <i className="bi bi-sliders"></i>
                            Edit Options
                            <span className="text-muted small fw-normal">
                                ({customValue.length} item{customValue.length !== 1 ? 's' : ''})
                            </span>
                        </span>
                        <div className="d-flex flex-wrap gap-2">
                            {modify ? (
                                <button type="button" className="btn btn-success btn-sm" onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-floppy me-1"></i>
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setModify(true)}>
                                    <i className="bi bi-pencil me-1"></i>
                                    Edit Values
                                </button>
                            )}
                            {modify && (
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModify(false)} disabled={isSaving}>
                                    Cancel Edit
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="notifications-panel__body">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-customization-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '35%' }}>
                                            <i className="bi bi-key"></i> Key
                                        </th>
                                        <th>
                                            <i className="bi bi-receipt"></i> Value
                                        </th>
                                        <th className="text-end" style={{ width: '7rem' }}>
                                            <i className="bi bi-gear"></i> Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customValue.length > 0 ? (
                                        customValue.map((item) => (
                                            <tr key={item.id} className="clinic-customization-row">
                                                <td>
                                                    {modify ? (
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={item.optionKey}
                                                            onChange={(e) => handleEdit(item, 'optionkey', e)}
                                                        />
                                                    ) : (
                                                        <span className="clinic-customization-key">{item.optionKey}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {modify ? (
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            value={item.optionValue}
                                                            onChange={(e) => handleEdit(item, 'optionvalue', e)}
                                                        />
                                                    ) : (
                                                        <span className="clinic-customization-value">{item.optionValue}</span>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <div className="d-inline-flex gap-1">
                                                        <ActionTooltipButton
                                                            tooltip="Delete option"
                                                            tooltipId={`delete-custom-${item.id}`}
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(item)}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </ActionTooltipButton>
                                                        <ActionTooltipButton
                                                            tooltip={copied === item.id ? 'Copied!' : 'Copy option ID'}
                                                            tooltipId={`copy-custom-${item.id}`}
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => copyToClipboard(item.id)}
                                                        >
                                                            <i className={`bi ${copied === item.id ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                                                        </ActionTooltipButton>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="p-0 border-0">
                                                <div className="clinic-customization-empty">
                                                    <i className="bi bi-sliders"></i>
                                                    No customization options yet. Add one using the panel on the right.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-lg-4 clinic-customization-sidebar">
                <div className="notifications-panel">
                    <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                        <span className="d-flex align-items-center gap-2">
                            <i className="bi bi-plus-circle"></i>
                            Add Option
                        </span>
                        <button
                            type="button"
                            className={`btn btn-sm ${addNew ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                            onClick={() => setAddNew(!addNew)}
                        >
                            <i className={`bi ${addNew ? 'bi-x-lg' : 'bi-plus-lg'} me-1`}></i>
                            {addNew ? 'Cancel' : 'Add New'}
                        </button>
                    </div>
                    <div className="notifications-panel__body">
                        <Collapse in={addNew}>
                            <div className="mb-3">
                                <AddCustomization onAdded={handleCustomAdd} onCancel={() => setAddNew(false)} />
                            </div>
                        </Collapse>
                        {!addNew && (
                            <p className="notifications-form-hint mb-0">
                                Add a new key-value pair to extend clinic-specific behavior and settings.
                            </p>
                        )}
                    </div>
                </div>

                <div className="notifications-panel clinic-customization-tips">
                    <div className="notifications-panel__header">
                        <i className="bi bi-info-circle"></i>
                        Tips
                    </div>
                    <div className="notifications-panel__body">
                        <p className="notifications-form-hint mb-0">
                            Use <strong>Edit Values</strong> to modify existing keys inline, then save. Delete removes an option immediately.
                            Copy ID is useful when referencing options in support or logs.
                        </p>
                    </div>
                </div>

                {onCancel && (
                    <div className="mt-3">
                        <button type="button" className="btn btn-outline-secondary btn-sm w-100" onClick={onCancel}>
                            <i className="bi bi-arrow-left me-1"></i>
                            Back to View
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CustomizationForm;
